/**
 * Sudoku Game — Main Container Component
 *
 * Orchestrates: difficulty selection → puzzle generation → cell interaction →
 * number placement → validation → completion.
 *
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 * Responsive layout: board + number pad + controls.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { SudokuEngine } from '../SudokuEngine';
import { SudokuBoard } from './SudokuBoard';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { SudokuErrorHandler } from '../services/SudokuErrorHandler';
import { SudokuStatisticsService } from '../services/SudokuStatisticsService';
import { SudokuGamePersistence } from '../services/SudokuGamePersistence';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import UniversalGameLayout, { UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import {
  SudokuDifficulty,
  SudokuGameSettings,
  SudokuGameState,
  SudokuPosition,
  SudokuGameProps,
} from '../types';
import './SudokuGame.css';

type GameView = 'mode-select' | 'playing' | 'completed';

const SudokuGameInner: React.FC<SudokuGameProps> = ({ onExit }) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<SudokuGameSettings | null>(null);
  const [gameState, setGameState] = useState<SudokuGameState | null>(null);
  const [notesMode, setNotesMode] = useState(false);
  const [timer, setTimer] = useState(0);

  // Refs
  const engineRef = useRef<SudokuEngine>(new SudokuEngine());
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Services (lazy init)
  const errorHandlerRef = useRef<SudokuErrorHandler | null>(null);
  const statsServiceRef = useRef<SudokuStatisticsService | null>(null);
  const persistenceRef = useRef<SudokuGamePersistence | null>(null);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new SudokuErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new SudokuStatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new SudokuGamePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize Sudoku services:', e);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Check for saved game on mount
  useEffect(() => {
    if (persistenceRef.current?.hasAutoSave()) {
      addNotification({
        type: NotificationType.INFO,
        title: 'Saved Game Found',
        message: 'A saved puzzle was found. Start a new game to continue.',
        duration: 4000,
      });
    }
  }, [addNotification]);

  // Timer management
  useEffect(() => {
    if (view === 'playing' && gameState?.gameStatus === 'playing') {
      timerRef.current = setInterval(() => {
        setTimer((prev) => {
          const newTime = prev + 1;
          engineRef.current.updateTimer(newTime);
          return newTime;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [view, gameState?.gameStatus]);

  // =========================================================================
  // Game Flow
  // =========================================================================

  const handleStartGame = useCallback(
    (newSettings: SudokuGameSettings) => {
      const engine = engineRef.current;
      const state = engine.initializeGame(newSettings.difficulty);

      setSettings(newSettings);
      setGameState(state);
      setNotesMode(false);
      setTimer(0);
      setView('playing');

      // Track game session
      gameSessionIdRef.current = gameSessionManager.current.startGameSession('sudoku');

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Puzzle Ready',
        message: `${newSettings.difficulty.charAt(0).toUpperCase() + newSettings.difficulty.slice(1)} puzzle generated. Good luck!`,
        duration: 2000,
      });
    },
    [addNotification]
  );

  const handleCellClick = useCallback(
    (position: SudokuPosition) => {
      if (!gameState || gameState.gameStatus !== 'playing') return;

      const engine = engineRef.current;
      engine.setSelectedCell(position);
      setGameState(engine.getGameState());
    },
    [gameState]
  );

  const handleNumberInput = useCallback(
    (num: number) => {
      if (!gameState || gameState.gameStatus !== 'playing') return;
      if (!gameState.selectedCell) return;

      const engine = engineRef.current;
      const { row, col } = gameState.selectedCell;
      const cell = gameState.board[row][col];

      if (cell.isGiven) return;

      try {
        let newState: SudokuGameState;

        if (notesMode) {
          // In notes mode, toggle the note if cell is empty
          if (cell.value !== null) {
            // Clear the cell first, then add note
            engine.removeNumber(row, col);
          }
          newState = engine.toggleNote(row, col, num);
        } else {
          // In normal mode, place or toggle the number
          if (cell.value === num) {
            // Same number — remove it
            newState = engine.removeNumber(row, col);
          } else {
            newState = engine.placeNumber(row, col, num);
          }
        }

        setGameState(newState);

        // Auto-save
        if (persistenceRef.current && settings) {
          persistenceRef.current.autoSave(newState, {
            difficulty: settings.difficulty,
            startedAt: newState.startedAt,
            lastSavedAt: Date.now(),
          });
        }

        // Check completion
        if (newState.gameStatus === 'completed') {
          handleGameComplete(newState);
        }
      } catch (error) {
        // Silently ignore invalid operations (e.g., adding note to given cell)
        console.warn('Sudoku move error:', error);
      }
    },
    [gameState, notesMode, settings]
  );

  const handleErase = useCallback(() => {
    if (!gameState || gameState.gameStatus !== 'playing') return;
    if (!gameState.selectedCell) return;

    const engine = engineRef.current;
    const { row, col } = gameState.selectedCell;
    const cell = gameState.board[row][col];

    if (cell.isGiven) return;

    try {
      const newState = engine.removeNumber(row, col);
      setGameState(newState);
    } catch (error) {
      console.warn('Erase error:', error);
    }
  }, [gameState]);

  const handleUndo = useCallback(() => {
    if (!gameState || gameState.gameStatus !== 'playing') return;

    const engine = engineRef.current;
    const newState = engine.undo();
    setGameState(newState);
  }, [gameState]);

  const handleHint = useCallback(() => {
    if (!gameState || gameState.gameStatus !== 'playing') return;

    const engine = engineRef.current;
    const { state: newState, position } = engine.getHint();
    setGameState(newState);

    if (position) {
      addNotification({
        type: NotificationType.INFO,
        title: '💡 Hint',
        message: `Revealed cell at row ${position.row + 1}, column ${position.col + 1}`,
        duration: 2000,
      });

      if (newState.gameStatus === 'completed') {
        handleGameComplete(newState);
      }
    } else {
      addNotification({
        type: NotificationType.INFO,
        title: 'No Hints Needed',
        message: 'All cells are already correct!',
        duration: 2000,
      });
    }
  }, [gameState, addNotification]);

  const handleToggleNotes = useCallback(() => {
    setNotesMode((prev) => !prev);
  }, []);

  const handleGameComplete = useCallback(
    (state: SudokuGameState) => {
      setView('completed');

      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // End game session
      if (gameSessionIdRef.current) {
        gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
        gameSessionIdRef.current = null;
      }

      // Clear auto-save
      persistenceRef.current?.clearAutoSave();

      // Record statistics
      if (statsServiceRef.current && settings) {
        statsServiceRef.current.recordGameResult(
          {
            gameId: state.gameId,
            difficulty: settings.difficulty,
            completed: true,
            time: timer,
            hintsUsed: state.hintsUsed,
            completedAt: Date.now(),
          },
          isAuthenticated ? user?.id : undefined
        );
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: '🎉 Puzzle Solved!',
        message: `Completed in ${formatTime(timer)}${state.hintsUsed > 0 ? ` with ${state.hintsUsed} hint${state.hintsUsed > 1 ? 's' : ''}` : ''}`,
        duration: 5000,
      });
    },
    [addNotification, settings, timer, isAuthenticated, user]
  );

  const handleNewGame = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setView('mode-select');
    setGameState(null);
    setSettings(null);
    setNotesMode(false);
    setTimer(0);
  }, []);

  const handleExit = useCallback(() => {
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onExit?.();
  }, [onExit]);

  // =========================================================================
  // Keyboard support
  // =========================================================================

  useEffect(() => {
    if (view !== 'playing' || !gameState || gameState.gameStatus !== 'playing') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;

      // Number keys 1-9
      if (key >= '1' && key <= '9') {
        e.preventDefault();
        handleNumberInput(parseInt(key, 10));
        return;
      }

      // Delete / Backspace to erase
      if (key === 'Delete' || key === 'Backspace') {
        e.preventDefault();
        handleErase();
        return;
      }

      // Arrow keys for navigation
      if (gameState.selectedCell && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
        e.preventDefault();
        const { row, col } = gameState.selectedCell;
        let newRow = row;
        let newCol = col;

        switch (key) {
          case 'ArrowUp': newRow = Math.max(0, row - 1); break;
          case 'ArrowDown': newRow = Math.min(8, row + 1); break;
          case 'ArrowLeft': newCol = Math.max(0, col - 1); break;
          case 'ArrowRight': newCol = Math.min(8, col + 1); break;
        }

        handleCellClick({ row: newRow, col: newCol });
        return;
      }

      // N for notes toggle
      if (key === 'n' || key === 'N') {
        e.preventDefault();
        handleToggleNotes();
        return;
      }

      // Z for undo (Ctrl+Z or just Z)
      if ((e.ctrlKey || e.metaKey) && key === 'z') {
        e.preventDefault();
        handleUndo();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [view, gameState, handleNumberInput, handleErase, handleCellClick, handleToggleNotes, handleUndo]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const sudokuConfig: GameModeConfig = {
      gameName: 'Sudoku',
      gameIcon: '🔢',
      availableModes: ['ai'],
      difficulties: [
        { id: 'easy', label: 'Easy', icon: '🌱' },
        { id: 'medium', label: 'Medium', icon: '⭐' },
        { id: 'hard', label: 'Hard', icon: '🏆' },
        { id: 'expert', label: 'Expert', icon: '👑' },
      ],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.difficulty) {
        handleStartGame({ difficulty: selection.difficulty as SudokuDifficulty });
      }
    };

    return (
      <div className="sudoku-game">
        <UniversalGameModeSelector
          config={sudokuConfig}
          onStart={handleModeSelection}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
          isAuthenticated={isAuthenticated}
        />
      </div>
    );
  }

  if (!gameState) return null;

  const errorCount = gameState.board.flat().filter((c) => c.isError && c.value !== null).length;

  // Left panels: Number pad + controls
  const leftPanels = gameState.gameStatus === 'playing' ? [
    {
      id: 'numpad',
      title: 'Number Pad',
      icon: '🔢',
      content: (
        <div className="sudoku-game__controls">
          {/* Action buttons */}
          <div className="sudoku-game__action-bar">
            <button
              className="sudoku-game__action-btn"
              onClick={handleUndo}
              disabled={gameState.moveHistory.length === 0}
              aria-label="Undo last move"
            >
              <span className="sudoku-game__action-icon">↩</span>
              <span className="sudoku-game__action-text">Undo</span>
            </button>
            <button
              className="sudoku-game__action-btn"
              onClick={handleErase}
              aria-label="Erase selected cell"
            >
              <span className="sudoku-game__action-icon">⌫</span>
              <span className="sudoku-game__action-text">Erase</span>
            </button>
            <button
              className={`sudoku-game__action-btn ${notesMode ? 'sudoku-game__action-btn--active' : ''}`}
              onClick={handleToggleNotes}
              aria-label={`Notes mode ${notesMode ? 'on' : 'off'}`}
              aria-pressed={notesMode}
            >
              <span className="sudoku-game__action-icon">✏️</span>
              <span className="sudoku-game__action-text">Notes{notesMode ? ' ON' : ''}</span>
            </button>
            <button
              className="sudoku-game__action-btn"
              onClick={handleHint}
              aria-label="Get a hint"
            >
              <span className="sudoku-game__action-icon">💡</span>
              <span className="sudoku-game__action-text">Hint</span>
            </button>
          </div>

          {/* Number pad */}
          <div className="sudoku-game__numpad" role="group" aria-label="Number pad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => {
              const count = gameState.board.flat().filter((c) => c.value === num).length;
              const isComplete = count >= 9;
              return (
                <button
                  key={num}
                  className={`sudoku-game__numpad-btn ${isComplete ? 'sudoku-game__numpad-btn--complete' : ''}`}
                  onClick={() => handleNumberInput(num)}
                  disabled={isComplete}
                  aria-label={`Place ${num}${isComplete ? ' (all placed)' : ''}`}
                >
                  {num}
                </button>
              );
            })}
          </div>
        </div>
      ),
    },
  ] : [];

  // Game over config
  const gameOverConfig = view === 'completed' ? {
    show: true,
    result: 'win' as const,
    title: 'Puzzle Solved!',
    subtitle: `${formatTime(timer)} | ${settings?.difficulty}${gameState.hintsUsed > 0 ? ` | ${gameState.hintsUsed} hint${gameState.hintsUsed > 1 ? 's' : ''}` : ''}`,
    icon: '🎉',
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Sudoku',
    board: (
      <SudokuBoard
        board={gameState.board}
        selectedCell={gameState.selectedCell}
        onCellClick={handleCellClick}
        disabled={gameState.gameStatus !== 'playing'}
      />
    ),
    leftPanels,
    statusBar: {
      turnIndicator: (
        <span>
          {settings?.difficulty.charAt(0).toUpperCase()}{settings?.difficulty.slice(1)}
          {' | '}⏱️ {formatTime(timer)}
          {' | '}💡 {gameState.hintsUsed}
          {errorCount > 0 && <span style={{ color: 'var(--ugl-danger, #e53e3e)' }}> | ❌ {errorCount}</span>}
        </span>
      ),
      gameStatus: view === 'completed' ? 'Solved!' : undefined,
      gameStatusType: view === 'completed' ? 'ended' : 'active',
    },
    actions: [
      { id: 'new-game', label: 'New Puzzle', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      { id: 'hint', label: 'Hint', icon: '💡', onClick: handleHint, disabled: gameState.gameStatus !== 'playing', variant: 'secondary' },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    gameOver: gameOverConfig,
  };

  return (
    <div className="sudoku-game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/** Format seconds as MM:SS */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/** Wrapped in ErrorBoundary */
const SudokuGame: React.FC<SudokuGameProps> = (props) => (
  <ErrorBoundary componentName="Sudoku">
    <SudokuGameInner {...props} />
  </ErrorBoundary>
);

export { SudokuGame };
export default SudokuGame;
